/**
 * Small UI kit, built for a phone lying on a table: large tap targets, high
 * contrast, and a sticky action bar that clears the iOS home indicator.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import FeedbackButton from '@/components/FeedbackButton';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-accent-500 text-base-950 font-semibold hover:bg-accent-400 active:bg-accent-600',
  secondary: 'bg-base-700 text-white hover:bg-base-600 active:bg-base-800',
  ghost: 'bg-transparent text-slate-300 hover:bg-base-800 active:bg-base-700',
  danger: 'bg-red-500/15 text-red-300 hover:bg-red-500/25 active:bg-red-500/35',
};

export function Button({
  children,
  onClick,
  variant = 'secondary',
  disabled,
  full,
  type = 'button',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  full?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={[
        'themed-control min-h-12 px-4 text-base transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        BUTTON_STYLES[variant],
        full ? 'w-full' : '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export function Panel({
  children,
  title,
  action,
}: {
  children: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="themed-panel bg-base-800/70 p-4 md:p-5">
      {(title || action) && (
        <header className="mb-3 flex items-center justify-between gap-3">
          {title && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
              {title}
            </h2>
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * Page shell. The action bar is sticky rather than fixed so it never covers the
 * last row of a long scorecard.
 */
export function Screen({
  title,
  subtitle,
  back,
  actions,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  back?: string;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col text-white">
      <header className="sticky top-0 z-20 border-b border-white/5 bg-base-900/95 px-4 py-3 backdrop-blur">
        {/* Capped width so the column does not stretch across an iPad. */}
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          {back && (
            <Link
              to={back}
              aria-label="Terug"
              className="-ml-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-300 hover:bg-base-800"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold">{title}</h1>
            {subtitle && <p className="truncate text-sm text-slate-400">{subtitle}</p>}
          </div>
          {/* Always present, so muting is one tap from anywhere and no page has
              to remember to wire it up. */}
          <FeedbackButton />
          {actions}
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 space-y-4 p-4 md:space-y-5 md:p-5">
        {children}
      </main>

      {/* The footer sits on the same layer as the header. Without a z-index
          anything positioned in the page scrolled straight over it. */}
      {footer && (
        <footer className="sticky bottom-0 z-20 border-t border-white/5 bg-base-900/95 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur">
          <div className="mx-auto max-w-2xl">{footer}</div>
        </footer>
      )}
    </div>
  );
}

/**
 * The number keypad used for both bids and tricks. Illegal values are rendered
 * but disabled, with an explanation underneath, so the restriction teaches
 * rather than just blocking.
 */
export function NumberPad({
  max,
  value,
  onSelect,
  disabledValues = [],
  label,
  groupLabel,
}: {
  max: number;
  value: number | null;
  onSelect: (value: number) => void;
  disabledValues?: number[];
  label?: string;
  /**
   * Prefixed to each button's accessible name, e.g. "Mila bod 3". Several pads
   * are on screen at once, so a bare "3" identifies nothing on its own.
   */
  groupLabel?: string;
}) {
  const options = Array.from({ length: max + 1 }, (_, index) => index);

  return (
    <div>
      {label && <p className="mb-2 text-sm text-slate-400">{label}</p>}
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isDisabled = disabledValues.includes(option);
          const isSelected = value === option;
          return (
            <button
              key={option}
              type="button"
              disabled={isDisabled}
              onClick={() => onSelect(option)}
              aria-pressed={isSelected}
              aria-label={groupLabel ? `${groupLabel} ${option}` : undefined}
              className={[
                'h-12 w-12 rounded-xl text-lg font-semibold tabular-nums transition-colors',
                isSelected
                  ? 'bg-accent-500 text-base-950'
                  : isDisabled
                    ? 'cursor-not-allowed bg-base-800/50 text-slate-600 line-through'
                    : 'bg-base-700 text-white hover:bg-base-600',
              ].join(' ')}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 py-2">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={[
          'mt-0.5 h-6 w-11 shrink-0 rounded-full p-0.5 transition-colors',
          checked ? 'bg-accent-500' : 'bg-base-600',
        ].join(' ')}
      >
        <span
          className={[
            'block h-5 w-5 rounded-full bg-white transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-sm text-slate-400">{hint}</span>}
      </span>
    </label>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string; hint?: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="space-y-2">
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isSelected}
            className={[
              'w-full rounded-xl border px-4 py-3 text-left transition-colors',
              isSelected
                ? 'border-accent-500 bg-accent-500/10'
                : 'border-white/10 bg-base-800/60 hover:bg-base-700/60',
            ].join(' ')}
          >
            <span className="block text-sm font-medium">{option.label}</span>
            {option.hint && <span className="block text-sm text-slate-400">{option.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** A short explanation that stays out of the way until tapped. */
export function Hint({ children, title }: { children: ReactNode; title: string }) {
  return (
    <details className="rounded-xl bg-base-800/60 px-3 py-2 text-sm">
      <summary className="cursor-pointer list-none text-slate-300 marker:content-none">
        <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-base-600 text-xs font-bold">
          ?
        </span>
        {title}
      </summary>
      <div className="mt-2 leading-relaxed text-slate-400">{children}</div>
    </details>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-slate-500">{children}</p>;
}
